import React from "react";
import { Link } from "react-router-dom";

/**
 * UserLink — clickable name + (optional) avatar that routes to a user's
 * profile page (`/u/<handle|user_id>`). Use this everywhere a user's
 * identity surfaces (bylines, comments, conversations, notifications)
 * so the social graph is one tap away from any screen.
 *
 * Props:
 *   handle      — preferred slug
 *   userId      — fallback slug when handle is missing
 *   name        — display name
 *   picture     — optional avatar URL
 *   avatarSize  — px size for the avatar (default 28). 0 hides the avatar.
 *   className   — applied to the outer Link
 *   showAt      — when true and handle is set, prefixes the name with "@handle"
 *   onClick     — extra hook (e.g. to close a modal). Profile navigation
 *                 still happens via the underlying Link.
 *   testid      — optional override
 */
export default function UserLink({
  handle,
  userId,
  name,
  picture,
  avatarSize = 0,
  className = "",
  showAt = false,
  onClick,
  testid,
}) {
  const slug = handle || userId;
  const label = showAt && handle ? `@${handle}` : (name || (handle ? `@${handle}` : "Reader"));
  if (!slug) {
    // No way to resolve a profile — render as plain text, no link.
    return <span className={className}>{label}</span>;
  }
  return (
    <Link
      to={`/u/${slug}`}
      onClick={onClick}
      data-testid={testid || `user-link-${slug}`}
      className={`inline-flex items-center gap-2 ${className}`}
    >
      {avatarSize > 0 && (
        picture ? (
          <img
            src={picture}
            alt=""
            style={{ width: avatarSize, height: avatarSize }}
            className="rounded-full ring-1 ring-border flex-shrink-0"
          />
        ) : (
          <span
            aria-hidden
            style={{ width: avatarSize, height: avatarSize, fontSize: avatarSize * 0.45 }}
            className="rounded-full bg-[hsl(var(--accent)/0.35)] grid place-items-center serif text-foreground flex-shrink-0"
          >
            {(name || "?")[0]?.toUpperCase() || "?"}
          </span>
        )
      )}
      <span className="link-underline">{label}</span>
    </Link>
  );
}
